/**
 * Form «dati mancanti» del carico (D7) — raggiungibile dal link nelle email
 * di Aida SENZA login (magic-link token monouso a scadenza) e dal pannello
 * carico per chi è loggato. Salva su m3.dato_carico e rigenera i documenti
 * impattati.
 */

import { CheckCircle2, Loader2, Truck } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { ApiError, apiFetch } from "@/lib/api";
import type { FormDatiCaricoResponse } from "@/types/api";

// Etichette leggibili dei campi (speculari a composizione.LABEL_CAMPI).
const LABEL_CAMPI: Record<string, string> = {
  targa: "Targa del mezzo",
  peso_lordo_kg: "Peso lordo (kg)",
  peso_netto_kg: "Peso netto (kg)",
  numero_ddt: "Numero DDT",
  data_ddt: "Data DDT",
  sticker_veterinario: "N. sticker/sigillo veterinario",
  numero_certificato_sanitario: "N. certificato sanitario",
  nave: "Nome nave",
  container: "Numero container",
  sigillo: "Numero sigillo",
  paesi_transito: "Paesi di transito (es. SI-HU-SK-PL-LT-LV)",
  valico_confine: "Valico di confine",
  numero_fattura: "Numero fattura",
};

function labelCampo(chiave: string): string {
  return LABEL_CAMPI[chiave] ?? chiave.replaceAll("_", " ");
}

export function M3FormDatiPage() {
  const { caricoId } = useParams<{ caricoId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [form, setForm] = useState<FormDatiCaricoResponse | null>(null);
  const [valori, setValori] = useState<Record<string, string>>({});
  const [stato, setStato] = useState<"caricamento" | "pronto" | "salvataggio" | "salvato" | "errore">(
    "caricamento"
  );
  const [errore, setErrore] = useState<string>("");

  useEffect(() => {
    if (!caricoId) return;
    apiFetch<FormDatiCaricoResponse>(
      `/api/v1/m3/agente/form/${caricoId}?token=${encodeURIComponent(token)}`
    )
      .then((data) => {
        setForm(data);
        setStato("pronto");
      })
      .catch((err) => {
        setErrore(
          err instanceof ApiError && (err.status === 401 || err.status === 410)
            ? "Questo link non è più valido: chiedi ad Aida un link nuovo (basta rispondere alla sua email)."
            : "Impossibile caricare il form."
        );
        setStato("errore");
      });
  }, [caricoId, token]);

  async function salva() {
    if (!caricoId) return;
    setStato("salvataggio");
    try {
      const aggiornato = await apiFetch<FormDatiCaricoResponse>(
        `/api/v1/m3/agente/form/${caricoId}?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ dati: valori }),
        }
      );
      setForm(aggiornato);
      setStato("salvato");
    } catch (err) {
      setErrore(
        err instanceof ApiError && typeof err.body === "object" && err.body
          ? String((err.body as { detail?: string }).detail ?? "Salvataggio non riuscito.")
          : "Salvataggio non riuscito."
      );
      setStato("errore");
    }
  }

  const compilati = Object.values(valori).filter((v) => v.trim()).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f3a2c] to-[#16291f] px-4 py-10">
      <div className="mx-auto w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-lg bg-[#1f3a2c] text-white">
            <Truck className="size-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-slate-900">
              Dati del carico {form?.riferimento_esterno ?? ""}
            </h1>
            <p className="text-xs text-slate-500">
              {form?.cliente_ragione_sociale ?? "Mister Pet · documenti spedizioni"}
            </p>
          </div>
        </div>

        {stato === "caricamento" && (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="size-4 animate-spin" /> Caricamento…
          </div>
        )}

        {stato === "errore" && (
          <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            {errore}
          </p>
        )}

        {stato === "salvato" && form && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
              <CheckCircle2 className="size-4 shrink-0" />
              Dati salvati: ho rigenerato i documenti interessati e li ho messi
              nel fascicolo del carico.
            </p>
            {form.campi_mancanti.length > 0 ? (
              <p className="text-xs text-slate-500">
                Mancano ancora: {form.campi_mancanti.map(labelCampo).join(", ")}.
                Aida te li richiederà via email.
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Tutti i dati necessari sono completi. ✓
              </p>
            )}
          </div>
        )}

        {(stato === "pronto" || stato === "salvataggio") && form && (
          <div className="space-y-4">
            {form.campi_mancanti.length === 0 ? (
              <p className="text-sm text-slate-600">
                Non manca nessun dato per questo carico. ✓
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  Per completare i documenti servono questi valori:
                </p>
                <div className="space-y-3">
                  {form.campi_mancanti.map((campo) => (
                    <label key={campo} className="block">
                      <span className="mb-1 block text-xs font-medium text-slate-700">
                        {labelCampo(campo)}
                      </span>
                      <input
                        type="text"
                        value={valori[campo] ?? ""}
                        onChange={(e) =>
                          setValori({ ...valori, [campo]: e.target.value })
                        }
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1f3a2c] focus:outline-none focus:ring-1 focus:ring-[#1f3a2c]"
                      />
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => void salva()}
                  disabled={compilati === 0 || stato === "salvataggio"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#1f3a2c] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2a4d3a] disabled:opacity-50"
                >
                  {stato === "salvataggio" && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  Salva {compilati > 0 ? `(${compilati})` : ""}
                </button>
                <p className="text-[11px] text-slate-400">
                  Puoi compilare anche solo i valori che hai adesso: il link in
                  ogni email di Aida è sempre aggiornato sui campi rimanenti.
                </p>
              </>
            )}

            {form.dati.length > 0 && (
              <details className="rounded-lg border border-slate-200 px-3 py-2">
                <summary className="cursor-pointer text-xs font-medium text-slate-600">
                  Dati già registrati ({form.dati.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {form.dati.map((d) => (
                    <li key={d.chiave} className="flex justify-between gap-2 text-xs">
                      <span className="text-slate-500">{labelCampo(d.chiave)}</span>
                      <span className="font-medium text-slate-800">{d.valore}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
